<template>
  <aside class="rail">
    <div v-if="!predmetId" class="rail__empty">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="1.6">
        <rect x="3" y="4" width="18" height="16" rx="2"></rect>
        <line x1="3" y1="9" x2="21" y2="9"></line>
      </svg>
      <div class="rail__empty-title">Odaberite predmet</div>
      <div class="rail__empty-sub">Kliknite red u listi za brzi pregled</div>
    </div>

    <div v-else-if="loading" class="rail__body">
      <div class="skeleton skeleton--title"></div>
      <div class="skeleton" style="width: 60%"></div>
      <div class="skeleton" style="width: 85%"></div>
      <div class="skeleton" style="width: 70%"></div>
    </div>

    <div v-else-if="error" class="rail__body">
      <div class="rail__error">Predmet nije moguće učitati.</div>
    </div>

    <div v-else class="rail__body">
      <div class="rail__head">
        <div>
          <div class="rail__broj">Predmet {{ predmet.broj }}</div>
          <div class="rail__sub">
            zaprimljen {{ formatDate(predmet.datum_prijema) }}
            <template v-if="ownerName"> · {{ ownerName }}</template>
          </div>
        </div>
        <StatusTag :value="predmet.status" pill />
      </div>

      <dl class="rail__grid">
        <div v-for="f in fields" :key="f.label" class="rail__field">
          <dt>{{ f.label }}</dt>
          <dd :class="{ 'rail__field--muted': !f.value }">{{ f.value || "—" }}</dd>
        </div>
        <div class="rail__field">
          <dt>U fazi</dt>
          <dd :class="phaseAgeClass">{{ phaseAge }}</dd>
        </div>
      </dl>

      <div v-if="predmet.napomena" class="rail__note">
        <span class="rail__note-label">Napomena</span>
        {{ predmet.napomena }}
      </div>

      <div v-if="recentHistory.length" class="rail__section">
        <div class="rail__section-title">Zadnja aktivnost</div>
        <div v-for="(h, i) in recentHistory" :key="i" class="rail__activity">
          <span class="rail__activity-date">{{ formatShortDate(h.timestamp) }}</span>
          <span>
            {{ statusLabel(h.status_id) }}
            <template v-if="h.username"> · {{ h.username }}</template>
          </span>
        </div>
      </div>

      <div v-if="comments.length" class="rail__section">
        <div class="rail__section-title">
          Komentari
          <span v-if="commentCount > comments.length" class="rail__more">
            {{ commentCount }} ukupno
          </span>
        </div>
        <div v-for="c in comments" :key="c.id" class="rail__comment">
          <div class="rail__comment-meta">
            {{ c.username }} · {{ formatShortDate(c.timestamp) }}
          </div>
          <div class="rail__comment-text">{{ c.comment }}</div>
        </div>
      </div>

      <div class="rail__spacer"></div>

      <div class="rail__actions">
        <el-button type="primary" class="rail__open" @click="open">Otvori predmet</el-button>
      </div>
    </div>
  </aside>
</template>

<script>
import moment from "moment";
import API from "@/services/api/api";
import { loadNosioci } from "@/models/nosioc";
import StatusTag from "@/components/StatusTag";
import { getStatusLabel, STALE_DAYS, WARN_DAYS } from "@/models/status";

export default {
  name: "PredmetRail",
  components: { StatusTag },
  props: {
    predmetId: { type: Number, default: 0 },
  },
  data() {
    return {
      predmet: {},
      nosioci: [],
      kontakti: {},
      obaveze: [],
      vrsta: null,
      comments: [],
      commentCount: 0,
      loading: false,
      error: false,
    };
  },
  created() {
    loadNosioci().then((l) => (this.nosioci = l)).catch((e) => console.log(e));
  },
  watch: {
    predmetId: {
      handler(id) {
        if (id) this.load(id);
      },
      immediate: true,
    },
  },
  computed: {
    ownerName() {
      const o = this.predmet.owner;
      return o ? `${o.first_name} ${o.last_name}` : "";
    },
    nosiocName() {
      const n = this.nosioci.find((x) => x.id === this.predmet.nosioc_id);
      return n ? n.naziv : "";
    },
    fields() {
      const f = [{ label: "Oštećeni", value: this.namesFor("oid") }];
      // Most cases store the same contact as both parties; only show the
      // punomoćnik when it is genuinely someone else.
      if (this.hasDistinctPunomocnik) {
        f.push({ label: "Punomoćnik", value: this.namesFor("pid") });
      }
      f.push(
        { label: "Nosioc", value: this.nosiocName },
        { label: "Vrsta", value: this.vrsta ? this.vrsta.naziv : "" },
        { label: "Obaveza", value: this.obaveze.map((o) => o && o.naziv).filter(Boolean).join(", ") },
        { label: "Vozilo", value: this.vozilo }
      );
      return f;
    },
    hasDistinctPunomocnik() {
      return (this.predmet.osteceni_ids || []).some((o) => o.pid && o.pid !== o.oid);
    },
    // meta carries brend/model as ids only, so show what reads without a
    // second round of lookups.
    vozilo() {
      const m = this.predmet.meta;
      if (!m) return "";
      return [m.registarska_oznaka, m.godiste].filter(Boolean).join(" · ");
    },
    // Days since the case entered its current phase, from the newest status
    // history entry (the API returns them newest first).
    daysInPhase() {
      const h = this.predmet.status_history;
      if (!h || !h.length) return null;
      return moment().diff(moment(h[0].timestamp), "days");
    },
    phaseAge() {
      const d = this.daysInPhase;
      if (d === null) return "—";
      if (d === 0) return "danas";
      return d === 1 ? "1 dan" : `${d} dana`;
    },
    phaseAgeClass() {
      const d = this.daysInPhase;
      if (d === null) return "rail__field--muted";
      if (d >= STALE_DAYS) return "rail__age rail__age--stale";
      if (d >= WARN_DAYS) return "rail__age rail__age--warn";
      return "";
    },
    recentHistory() {
      return (this.predmet.status_history || []).slice(0, 3);
    },
  },
  methods: {
    statusLabel: getStatusLabel,
    namesFor(key) {
      return (this.predmet.osteceni_ids || [])
        .map((o) => this.kontakti[o[key]])
        .filter(Boolean)
        .map((k) => `${k.ime} ${k.prezime}`)
        .join(", ");
    },
    formatDate(d) {
      return d ? moment(d).format("DD.MM.YYYY") : "";
    },
    formatShortDate(d) {
      return d ? moment(d).format("DD.MM.") : "";
    },
    open() {
      this.$router.push("/predmet/view/" + this.predmetId);
    },
    async load(id) {
      this.loading = true;
      this.error = false;
      try {
        const res = await API.Generic.get("predmet", id);
        const p = res.data.data;
        this.predmet = p;
        await Promise.all([this.loadKontakti(p), this.loadRefs(p), this.loadComments(id)]);
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async loadKontakti(p) {
      const ids = [];
      (p.osteceni_ids || []).forEach((o) => {
        if (o.oid) ids.push(o.oid);
        if (o.pid) ids.push(o.pid);
      });
      if (!ids.length) {
        this.kontakti = {};
        return;
      }
      const res = await API.Kontakti.many(Array.from(new Set(ids)));
      const map = {};
      res.data.data.results.forEach((k) => {
        map[k.id] = k;
      });
      this.kontakti = map;
    },
    // Newest first, trimmed to the few that fit — the case view has the rest.
    async loadComments(id) {
      this.comments = [];
      this.commentCount = 0;
      try {
        const res = await API.Generic.all("komentar", {
          parent_type: { val: 2 },
          parent_id: { val: id },
        });
        const all = (res.data.data.results || [])
          .slice()
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        this.commentCount = all.length;
        this.comments = all.slice(0, 3);
      } catch (e) {
        console.log(e);
      }
    },
    async loadRefs(p) {
      this.obaveze = [];
      this.vrsta = null;
      const jobs = [];
      if (p.obaveza && p.obaveza.length) {
        jobs.push(
          API.Generic.many("obaveza", p.obaveza).then((r) => {
            this.obaveze = r.data.data.results;
          })
        );
      }
      if (p.vrsta_osiguranja_id) {
        jobs.push(
          API.Generic.many("vrsta_osiguranja", [p.vrsta_osiguranja_id]).then((r) => {
            this.vrsta = r.data.data.results[0] || null;
          })
        );
      }
      await Promise.all(jobs);
    },
  },
};
</script>

<style lang="scss" scoped>
.rail {
  width: 380px;
  flex-shrink: 0;
  background: var(--surface);
  border: 1px solid var(--line);
  border-left: none;
  border-radius: 0 var(--r-lg) var(--r-lg) 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.rail__empty {
  margin: auto;
  padding: 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.rail__empty-title {
  font-size: var(--fs-md);
  font-weight: 600;
  color: var(--ink-2);
  margin-top: 6px;
}

.rail__empty-sub {
  font-size: var(--fs-sm);
  color: var(--ink-4);
}

.rail__body {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}

.rail__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.rail__broj {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
}

.rail__sub {
  font-size: var(--fs-sm);
  color: var(--ink-3);
  margin-top: 2px;
}

.rail__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  margin: 0;
}

.rail__field {
  min-width: 0;

  dt {
    font-size: var(--fs-xs);
    color: var(--ink-4);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  dd {
    margin: 2px 0 0 0;
    font-size: var(--fs-md);
    font-weight: 500;
    color: var(--ink);
    overflow-wrap: anywhere;
  }
}

.rail__field--muted {
  color: var(--ink-4) !important;
  font-weight: 400 !important;
}

.rail__age--warn {
  color: #8a6d0b !important;
}

.rail__age--stale {
  color: var(--danger) !important;
  font-weight: 600 !important;
}

.rail__note {
  background: var(--surface-alt);
  border-radius: var(--r-md);
  padding: 10px 12px;
  font-size: var(--fs-sm);
  color: var(--ink-2);
  line-height: 1.5;
}

.rail__note-label {
  font-weight: 600;
  color: var(--ink);
  margin-right: 4px;
}

.rail__section {
  border-top: 1px solid var(--line-soft);
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rail__section-title {
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--ink-4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rail__activity {
  display: flex;
  gap: 8px;
  font-size: var(--fs-sm);
  color: var(--ink-2);
  line-height: 1.45;
}

.rail__activity-date {
  color: var(--ink-4);
  flex-shrink: 0;
}

.rail__more {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--ink-4);
  margin-left: 6px;
}

.rail__comment {
  background: var(--surface-alt);
  border-radius: var(--r-md);
  padding: 8px 10px;
}

.rail__comment-meta {
  font-size: var(--fs-xs);
  color: var(--ink-4);
  margin-bottom: 3px;
}

.rail__comment-text {
  font-size: var(--fs-sm);
  color: var(--ink-2);
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.rail__spacer {
  flex-grow: 1;
  min-height: 8px;
}

.rail__actions {
  display: flex;
  gap: 8px;
}

.rail__open {
  flex-grow: 1;
}

.rail__error {
  font-size: var(--fs-md);
  color: var(--danger);
}

.skeleton {
  height: 11px;
  border-radius: 4px;
  background: #eef2f7;
}

.skeleton--title {
  height: 20px;
  width: 55%;
  margin-bottom: 4px;
}
</style>
